export const CLINIC_EMAIL_SAVE_I18N_KEYS = [
  'settings.email.local_part_reserved',
  'settings.email.local_part_taken',
  'settings.email.local_part_invalid',
  'settings.email.save_forbidden',
  'settings.email.save_audit_failed',
  'settings.email.save_failed',
] as const;

export type ClinicEmailSaveI18nKey = (typeof CLINIC_EMAIL_SAVE_I18N_KEYS)[number];

const CPR_SHAPED = /\b\d{6}-?\d{4}\b/g;
const EMAIL_SHAPED = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;

function readString(err: unknown, key: 'code' | 'message' | 'details' | 'hint'): string {
  if (!err || typeof err !== 'object' || !(key in err)) return '';
  const value = (err as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : '';
}

function errorBlob(err: unknown): string {
  return [readString(err, 'code'), readString(err, 'message'), readString(err, 'details'), readString(err, 'hint')]
    .join(' ')
    .toLowerCase();
}

export function clinicEmailSaveErrorI18nKey(err: unknown): ClinicEmailSaveI18nKey {
  const code = readString(err, 'code');
  const blob = errorBlob(err);

  if (blob.includes('reserved')) return 'settings.email.local_part_reserved';
  if (code === '23505' || blob.includes('taken') || blob.includes('duplicate key')) {
    return 'settings.email.local_part_taken';
  }
  if (
    code === '42501' ||
    code === 'PGRST301' ||
    blob.includes('forbidden') ||
    blob.includes('permission denied') ||
    blob.includes('clinic admin required')
  ) {
    return 'settings.email.save_forbidden';
  }
  if (
    code === '42883' ||
    blob.includes('create_audit_log') ||
    blob.includes('revisionslog') ||
    (blob.includes('does not exist') && blob.includes('audit'))
  ) {
    return 'settings.email.save_audit_failed';
  }
  if (
    code === '22023' ||
    blob.includes('invalid-input') ||
    blob.includes('local-part') ||
    blob.includes('too long')
  ) {
    return 'settings.email.local_part_invalid';
  }
  return 'settings.email.save_failed';
}

/** Short, non-PII excerpt for the generic toast. Never CPR or email addresses. */
export function clinicEmailSaveErrorReason(err: unknown): string {
  const code = readString(err, 'code');
  const raw = readString(err, 'message') || readString(err, 'details') || readString(err, 'hint');
  const cleaned = raw.replace(CPR_SHAPED, '[cpr]').replace(EMAIL_SHAPED, '[email]').replace(/\s+/g, ' ').trim();
  const snippet = cleaned.slice(0, 160);
  if (code && snippet) return `${code}: ${snippet}`;
  return code || snippet;
}

export function clinicEmailSaveErrorToast(err: unknown): {
  key: ClinicEmailSaveI18nKey | 'settings.email.save_failed_detail';
  reason?: string;
} {
  const key = clinicEmailSaveErrorI18nKey(err);
  if (key !== 'settings.email.save_failed') return { key };
  const reason = clinicEmailSaveErrorReason(err);
  if (reason) return { key: 'settings.email.save_failed_detail', reason };
  return { key };
}

export function isMissingRpcError(err: unknown): boolean {
  const code = readString(err, 'code');
  const message = readString(err, 'message');
  if (/create_audit_log/i.test(message)) return false;
  return (
    code === 'PGRST202' &&
    /set_clinic_email_/i.test(message) &&
    /could not find the function/i.test(message)
  );
}
